import React from 'react';
import { defineMessages, useIntl, type MessageValue } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { HighlightWords, Icon, theme, webOnlyStyles } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuProductCardLineageOverlay = (props: Props) => {
  const { isLineageKeyAvailable, productName, onPress } = props;

  const { formatMessage } = useIntl();

  const overlayStyles = [styles.overlay, overlayWebStyles];
  const label = formatMessage(
    isLineageKeyAvailable ? messages.copyLineageKeyLabel : messages.lineageKeyUnavailableLabel,
    { product_name: productName },
  );

  return (
    <Animated.View entering={OVERLAY_ENTERING} exiting={OVERLAY_EXITING} style={overlayStyles}>
      <TouchableOpacity
        accessibilityRole="button"
        aria-label={label}
        onPress={onPress}
        style={styles.pressable}
        activeOpacity={0.95}
      >
        <View style={styles.content}>
          {isLineageKeyAvailable ? (
            <Icon name="IconCopy" color={theme.colors.WHITE} width={ICON_SIZE} height={ICON_SIZE} />
          ) : null}

          <HighlightWords
            text={label}
            highlights={[productName]}
            sizeMatch={['16']}
            style={styles.text}
          />
        </View>
      </TouchableOpacity>
    </Animated.View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly copyLineageKeyLabel: { readonly product_name: MessageValue };
  readonly lineageKeyUnavailableLabel: { readonly product_name: MessageValue };
}>({
  copyLineageKeyLabel: {
    defaultMessage: 'Copy lineage key for {product_name}',
    description: 'Menu | Product card | Copy lineage key label',
  },
  lineageKeyUnavailableLabel: {
    defaultMessage: 'Lineage key unavailable for {product_name}',
    description: 'Menu | Product card | Missing lineage key label',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const ICON_SIZE = 32;
const OVERLAY_ENTERING = FadeIn.duration(theme.transitions.short);
const OVERLAY_EXITING = FadeOut.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing['4'],
    backgroundColor: theme.colors.KALE,
    padding: theme.spacing['4'],
  },
  overlay: {
    ...StyleSheet.absoluteFill,
    zIndex: 3,
    overflow: 'hidden',
    boxShadow: '0 10px 28px rgba(14, 21, 14, 0.2)',
    borderRadius: theme.radius.xlarge,
  },
  pressable: {
    flex: 1,
  },
  text: {
    color: theme.colors.WHITE,
    textAlign: 'center',
  },
});

const overlayWebStyles = webOnlyStyles({
  outlineColor: theme.colors.KALE,
  outlineOffset: 2,
  outlineStyle: 'solid',
  outlineWidth: 2,
  cursor: 'pointer',
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isLineageKeyAvailable: boolean;
  productName: string;
  onPress: () => void;
};
